import React, { useEffect, useState } from 'react'
import 'react-multi-carousel/lib/styles.css'
import 'bootstrap/dist/css/bootstrap.css'
import './package.css'
import Footer from '../common/footer/Footer'
import Header from '../common/header/Header'
import PropTypes from 'prop-types';
import { Tab, Tabs, TextField, Box, Autocomplete, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import Carousel from 'react-multi-carousel'
import { Link } from 'react-router-dom'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { packageCity, getPackagesByCategory } from '../../api/package'
import { getImageUrl } from '../../utils/imageUrl'

// -------------------------------------------------

/**
 * Custom Tab Panel component for category tabs
 */
function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`package-tabpanel-${index}`}
      aria-labelledby={`package-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `package-tab-${index}`,
    'aria-controls': `package-tabpanel-${index}`,
  };
}

// Carousel responsive settings
const packageCarousel = {
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 3000 },
    items: 4,
    slidesToSlide: 1,
  },
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 4,
    slidesToSlide: 1,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 2,
    slidesToSlide: 1,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
    slidesToSlide: 1,
  },
}

const destinationCarousel = {
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 3000 },
    items: 6,
    slidesToSlide: 1,
  },
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 6,
    slidesToSlide: 1,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 3,
    slidesToSlide: 1,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
    slidesToSlide: 1,
  },
}

/**
 * Package Card Component - Displays individual package information
 */
const PackageCard = ({ pkg, isCorporate }) => {
  return (
    <div className={`destination ${isCorporate ? 'corporate-card' : ''}`}>
      <Box className="eximg-group g-bg-4 tourIm">
        <img
          className=""
          src={getImageUrl(pkg?.images?.[0]) || 'images/thailand.jpg'}
          alt={pkg?.packageName || 'Package'}
        />
        {pkg?.hotelType && (
          <h6 className="mb-0 rating">{pkg?.hotelType} <i className="fa-solid fa-star"></i></h6>
        )}
      </Box>
      <div className='py-2'>
        <div className="py-2">
          <h5 className="card-title">
            <Link to={`/tour?id=${pkg?._id}`}>{pkg?.packageName || pkg?.city}</Link>
          </h5>
          <small className="text-muted">{pkg?.dayCount}D / {pkg?.nightCount}N</small>
        </div>
        <div className="hstack gap-2">
          <span className="h5 mb-0 text-success">₹{pkg?.price?.toLocaleString()}</span>
          <small>Starting price</small>
        </div>
        <Box
          className='bookBtn'
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            mt: 2
          }}
        >
          <Link to={`/tour?id=${pkg?._id}`} className="btn btn-dark py-2 px-3 w-100">
            {isCorporate ? 'Get Quote' : 'Book Now'}
          </Link>
        </Box>
      </div>
    </div>
  )
}

/**
 * Package Section - Displays packages of a specific category
 */
const PackageSection = ({ title, packages, isCorporate }) => {
  if (!packages || packages.length === 0) {
    return (
      <section className={`specialOffers ${isCorporate ? 'corporate-section' : ''}`}>
        <div className='container'>
          <div className="row mb-3">
            <div className="col-12">
              <h4 className="mb-0 title">{title}</h4>
            </div>
          </div>
          <p className="text-muted">No packages available in this category.</p>
        </div>
      </section>
    )
  }

  return (
    <section className={`specialOffers ${isCorporate ? 'corporate-section' : ''}`}>
      <div className='container'>
        <div className="row mb-4">
          <div className="col-12">
            <h4 className="mb-0 title">{title}</h4>
            {isCorporate && (
              <p className="corporate-subtitle">Tailored travel solutions for your business needs</p>
            )}
          </div>
        </div>
        <div>
          <Carousel
            responsive={packageCarousel}
            infinite={packages.length > 4}
            autoPlay={true}
            autoPlaySpeed={3000}
          >
            {packages.map((pkg) => (
              <PackageCard key={pkg._id} pkg={pkg} isCorporate={isCorporate} />
            ))}
          </Carousel>
        </div>
      </div>
    </section>
  )
}

/**
 * Main Package Component
 */
const Package = () => {
  const [tabValue, setTabValue] = useState(0);
  const [cities, setCities] = useState([])
  const [internationalPackages, setInternationalPackages] = useState([])
  const [domesticPackages, setDomesticPackages] = useState([])
  const [corporatePackages, setCorporatePackages] = useState([])
  const [loading, setLoading] = useState(true)

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Fetch all data on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        // Fetch cities for destinations carousel
        const cityResp = await packageCity()
        setCities(cityResp?.data || [])

        // Fetch packages by category
        const [intResp, domResp, corpResp] = await Promise.all([
          getPackagesByCategory('International'),
          getPackagesByCategory('Domestic'),
          getPackagesByCategory('Corporate')
        ])

        setInternationalPackages(intResp?.data?.docs || [])
        // Include 'National' for backward compatibility
        const domesticData = domResp?.data?.docs || []
        setDomesticPackages(domesticData)
        setCorporatePackages(corpResp?.data?.docs || [])
      } catch (error) {
        console.error('Error fetching packages:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  return (
    <>
      <Header />

      {/* Hero Section */}
      <section className=''>
        <div className='container'>
          <div className="mainBannerSectionCab">
            <Box
              className='w-1'
              sx={{
                width: '40%',
                backgroundColor: '#fff',
                borderRadius: '10px',
                padding: '20px'
              }}>
              <h1 className='p-0'>Explore Our Travel Packages</h1>
              <div className='row'>
                <div className='col-md-12 py-2'>
                  <Autocomplete
                    size='full'
                    sx={{ mt: 1 }}
                    disablePortal
                    id="package-search"
                    options={cities?.map(c => ({ label: c.name }))}
                    renderInput={(params) => <TextField {...params} label="Search Destination" placeholder="Where do you want to go?" />}
                  />
                </div>
                <div className="col-12 text-center mt-4">
                  <button className="btn btn-dark w-100" href="#">
                    Search Packages
                  </button>
                </div>
              </div>
            </Box>
          </div>
        </div>
      </section>

      {/* Popular Destinations */}
      <section className='specialOffers'>
        <div className='container'>
          <div className="row mb-5">
            <div className="col-12">
              <h4 className="mb-0 title">Popular Destinations</h4>
            </div>
          </div>
          <div>
            <Carousel
              responsive={destinationCarousel}
              infinite={true}
              autoPlay={true}
              autoPlaySpeed={2000}
            >
              {cities?.slice(0, 10).map((city, index) => (
                <div className="vehicles" key={city._id || index}>
                  <div className="card shadow text-center align-items-center h-100 p-3 border-0">
                    <div className="icon-Package bg-light rounded-circle">
                      <img src={getImageUrl(city?.image) || 'images/thailand.jpg'} alt={city?.name} />
                    </div>
                    <div className="card-body px-0 pb-0">
                      <h5 className="card-title">
                        <Link to={`/tour?city=${city?.name}`}>{city?.name}</Link>
                      </h5>
                    </div>
                  </div>
                </div>
              ))}
            </Carousel>
          </div>
        </div>
      </section>

      {/* Package Category Tabs */}
      <section className='package-categories'>
        <div className='container'>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="package categories"
              centered
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                }
              }}
            >
              <Tab label="All Packages" {...a11yProps(0)} />
              <Tab label="International" {...a11yProps(1)} />
              <Tab label="Domestic" {...a11yProps(2)} />
              <Tab label="Corporate" {...a11yProps(3)} />
            </Tabs>
          </Box>

          {/* All Packages Tab */}
          <CustomTabPanel value={tabValue} index={0}>
            <PackageSection title="International Packages" packages={internationalPackages} />
            <PackageSection title="Domestic Packages" packages={domesticPackages} />
            <PackageSection title="Corporate Packages" packages={corporatePackages} isCorporate={true} />
          </CustomTabPanel>

          {/* International Tab */}
          <CustomTabPanel value={tabValue} index={1}>
            <PackageSection title="International Packages" packages={internationalPackages} />
          </CustomTabPanel>

          {/* Domestic Tab */}
          <CustomTabPanel value={tabValue} index={2}>
            <PackageSection title="Domestic Packages" packages={domesticPackages} />
          </CustomTabPanel>

          {/* Corporate Tab */}
          <CustomTabPanel value={tabValue} index={3}>
            <PackageSection title="Corporate Travel Solutions" packages={corporatePackages} isCorporate={true} />
          </CustomTabPanel>
        </div>
      </section>

      {/* FAQ Section */}
      <section className='specialOffers'>
        <div className='container'>
          <div className="row mb-5">
            <div className="col-12">
              <h4 className="mb-0 title">Frequently Asked Questions</h4>
            </div>
          </div>
          <div className="row py-3 justify-content-center">
            <div className='col-10'>
              <Accordion defaultExpanded>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <strong>What is the difference between International and Domestic packages?</strong>
                </AccordionSummary>
                <AccordionDetails>
                  International packages cover destinations outside your home country and typically include visa assistance, international flights, and foreign currency guidance. Domestic packages focus on destinations within your country with easier logistics and no visa requirements.
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel2-content"
                  id="panel2-header"
                >
                  <strong>What are Corporate packages?</strong>
                </AccordionSummary>
                <AccordionDetails>
                  Corporate packages are specially designed for business travelers and companies. They include features like team-building activities, conference facilities, flexible booking, bulk discounts, and dedicated corporate support for seamless business travel management.
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel3-content"
                  id="panel3-header"
                >
                  <strong>Can I customize my package?</strong>
                </AccordionSummary>
                <AccordionDetails>
                  Yes! All our packages can be customized based on your preferences. Contact our travel experts to modify itineraries, upgrade hotels, add activities, or create a completely custom travel plan tailored to your needs.
                </AccordionDetails>
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  )
}

export default Package